import React, { useState, useEffect } from 'react';
import useTrackToolUsage from '@/hooks/useTrackToolUsage';
import { api } from '@/App';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Sparkle } from '@phosphor-icons/react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const RMDPlanning = () => {
  useTrackToolUsage('rmd_planning');
  const [profile, setProfile] = useState(null);
  const [traditionalBalance, setTraditionalBalance] = useState(500000);
  const [currentAge, setCurrentAge] = useState(65);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/financial-profile');
      setProfile(response.data);
      setCurrentAge(response.data.age);
    } catch (error) {
      console.error('Failed to load profile');
    }
  };

  const RMD_FACTORS = {
    72: 27.4, 73: 26.5, 74: 25.5, 75: 24.6, 76: 23.7, 77: 22.9,
    78: 22.0, 79: 21.1, 80: 20.2, 81: 19.4, 82: 18.5, 83: 17.7,
    84: 16.8, 85: 16.0, 86: 15.2, 87: 14.4, 88: 13.7, 89: 12.9,
    90: 12.2, 91: 11.5, 92: 10.8, 93: 10.1, 94: 9.5, 95: 8.9
  };

  const calculateRMDProjections = () => {
    const projections = [];
    let balance = traditionalBalance;
    const growthRate = 0.06;

    for (let age = currentAge; age <= Math.min(currentAge + 25, 95); age++) {
      if (age >= 73) {
        const distributionPeriod = RMD_FACTORS[age] || RMD_FACTORS[95];
        const rmd = balance / distributionPeriod;
        const estimatedTax = rmd * 0.22;
        
        projections.push({
          age,
          balance: Math.round(balance),
          rmd: Math.round(rmd),
          estimatedTax: Math.round(estimatedTax)
        });
        
        balance = (balance - rmd) * (1 + growthRate);
      } else {
        projections.push({
          age,
          balance: Math.round(balance),
          rmd: 0,
          estimatedTax: 0
        });
        balance *= (1 + growthRate);
      }
    }
    
    return projections;
  };

  const projections = calculateRMDProjections();

  return (
    <div className="space-y-8 fade-in">
      <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-4">
        <div className="flex items-start gap-3">
          <Sparkle size={20} weight="duotone" className="text-[#C5A059] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#4A5568]">
            <strong className="text-[#1A202C]">Educational Estimates Only:</strong> RMD calculations are based on IRS life expectancy tables. Actual RMDs may vary. Consult a tax professional for accurate calculations.
          </p>
        </div>
      </div>

      <div>
        <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mb-2">
          RMD Planning
        </h1>
        <p className="text-base text-[#4A5568]">
          Project Required Minimum Distributions (Educational estimates)
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
            <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Your Information</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="age">Current Age</Label>
                <Input
                  id="age"
                  type="number"
                  data-testid="age-input"
                  value={currentAge}
                  onChange={(e) => setCurrentAge(Number(e.target.value))}
                  className="border-slate-300"
                />
              </div>
              <div>
                <Label htmlFor="balance">Traditional IRA/401(k) Balance ($)</Label>
                <Input
                  id="balance"
                  type="number"
                  data-testid="balance-input"
                  value={traditionalBalance}
                  onChange={(e) => setTraditionalBalance(Number(e.target.value))}
                  className="border-slate-300"
                />
              </div>
              <div className="pt-4 border-t border-slate-200">
                <p className="text-xs text-[#4A5568] leading-relaxed">
                  RMDs begin at age 73 (for those born 1951-1959). Projections assume 6% annual growth and 22% tax rate.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="rmd-projections-chart">
            <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">RMD Projections</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={projections}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="age" stroke="#4A5568" />
                <YAxis stroke="#4A5568" />
                <Tooltip />
                <Line type="monotone" dataKey="rmd" stroke="#C5A059" strokeWidth={2} name="RMD Amount" />
                <Line type="monotone" dataKey="estimatedTax" stroke="#C53030" strokeWidth={2} name="Est. Tax" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
            <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Key Ages</h2>
            <div className="grid grid-cols-3 gap-4">
              {projections.filter(p => [73, 75, 80].includes(p.age)).map(data => (
                <div key={data.age} className="border border-slate-200 rounded-md p-4">
                  <p className="text-xs text-[#4A5568] mb-1">Age {data.age}</p>
                  <p className="text-lg font-semibold text-[#0A1128] ibm-plex mb-1">${(data.rmd || 0).toLocaleString()}</p>
                  <p className="text-xs text-[#4A5568]">RMD Amount</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RMDPlanning;
